import {
    type HTMLAttributes, memo, type RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'

import { bisector, line, extent, scaleTime, scaleLinear, pointer, select, ticks, format } from 'd3'
import type { NumberValue, ScaleLinear, ScaleTime, CurveFactoryLineOnly } from 'd3'

import cn from 'classnames'
import { useResizeObserver } from '~/hooks/client/useResizeObserver'
import {
    ChartContext, defaultChartContext, IChartContext,
} from '~/components/data-display/charts/context';
import { format as d3format } from 'd3-format'

export type LineSmallDatum = [Date, number]
export type LineSmallData = LineSmallDatum[]

interface LineSmallProps extends HTMLAttributes<HTMLDivElement>
{
    data: LineSmallData
    hideAxis?: boolean
    fontFamily?: string
    valueFmtOptions?: IChartContext['valueFmtOptions'],
}

interface ChartConfig
{
    dx: number
}

const bisect = bisector<LineSmallDatum, any>(d => d[0]).left

function findLineD (
    x: number,
    data: LineSmallData,
    xInvert: (arg0: NumberValue) => Date,
    bisectLeft: (array: ArrayLike<LineSmallDatum>, x: any, lo?: number, hi?: number) => number,
): [LineSmallDatum, number]
{
    const x0 = xInvert(x)
    const i = bisectLeft(data, x0, 1)
    const d0 = data[i - 1]
    const d1 = data[i] || d0
    const d = (
                  x0.valueOf() - d0[0].valueOf()
              ) > (
                  d1[0].valueOf() - x0.valueOf()
              )
        ? d1
        : d0
    return [d, i]
}

const axisFontSize = 8

export function LineSmallFallback ({
    data,
    fontFamily,
    hideAxis,
    className,
}: LineSmallProps)
{
    const { valueFmt, valueFmtOptions } = useContext(ChartContext)

    return (
        <div className={cn(className)}>
            <span>{valueFmt(data[data.length - 1][1], valueFmtOptions.decimals, valueFmtOptions.prefix)}</span>
            <span className={cn('opacity-60')}>@{data[data.length - 1][0].toLocaleString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            })}</span>
        </div>
    )
}

export function LineSmall ({
    data,
    fontFamily = 'monospace',
    hideAxis = false,
    className = '',
    valueFmtOptions = {
        decimals: 2,
        prefix: '$',
    },
}: LineSmallProps)
{
    const rootRef = useRef<HTMLDivElement>(null)
    const axisRef = useRef<SVGTextElement>(null)

    const svgRef = useRef<SVGSVGElement>(null)

    const svgSize = useResizeObserver(rootRef)
    const axisSize = useResizeObserver(axisRef)

    const chartConfig = useMemo<ChartConfig>(() => (
        {
            dx: hideAxis
                ? 0
                : 6,
        }
    ), [hideAxis])

    const xScale = useMemo(() =>
    {
        const xExtent = extent(data, (d) => d[0]) as [Date, Date]
        return scaleTime().domain(xExtent).range([0, svgSize.width - axisSize.width - chartConfig.dx])
    }, [data, svgSize.width, chartConfig.dx, axisSize.width])

    const yScale = useMemo(() =>
    {
        const yExtent = extent(data, (d) => d[1]) as [number, number]
        return scaleLinear().domain(yExtent).range([svgSize.height - axisFontSize / 2, axisFontSize / 2]).nice()
    }, [data, svgSize.height])

    const [hoverPoint, setHoverPoint] = useState<LineSmallDatum | null>(null)
    const hoverRef = useRef<LineSmallDatum | null>(null)
    hoverRef.current = hoverPoint

    const handlePointer = useCallback((evt: MouseEvent) =>
    {
        const [mx] = pointer(evt)
        const x = mx - axisSize.width - chartConfig.dx
        const [pt] = findLineD(x, data, xScale.invert, bisect)
        if (hoverRef.current?.[0] !== pt[0]) {
            setHoverPoint(pt)
        }
    }, [data, axisSize.width, chartConfig.dx, xScale])

    useEffect(() =>
    {
        if (!svgRef.current) {
            return () => {}
        }

        const s = select(svgRef.current)
        s.on('mouseenter pointermove', handlePointer)
            .on('mouseleave', () => setHoverPoint(null))

        return () => s.on('.', null)
    }, [handlePointer, svgRef.current])

    const chartWidth = useMemo(
        () => svgSize.width - chartConfig.dx - axisSize.width,
        [svgSize.width, chartConfig.dx, axisSize.width],
    )

    return (
        <ChartContext
            value={{
                ...defaultChartContext,
                valueFmtOptions,
                fontFamily,
            }}
        >
            <div className={cn('relative', className)} ref={rootRef}>
                <svg
                    viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
                    ref={svgRef}
                >
                    {!hideAxis && (
                        <g transform={`translate(${axisSize.width} 0)`}>
                            <YAxis
                                yScale={yScale}
                                ref={axisRef}
                            />
                        </g>
                    )}

                    <g transform={`translate(${axisSize.width + chartConfig.dx} 0)`}>
                        <YGrid yScale={yScale} lineWidth={chartWidth} />

                        <Line data={data} xScale={xScale} yScale={yScale} />

                        <Crosshair
                            datum={hoverPoint}
                            xScale={xScale}
                            yScale={yScale}
                            chartWidth={chartWidth}
                            chartHeight={svgSize.height}
                        />
                    </g>
                </svg>
                <div
                    className={'absolute top-0 left-0 pointer-events-none w-0 h-0'}
                    style={{ transform: `translate(${axisSize.width + chartConfig.dx}px, 1px)` }}
                >
                    <Tooltip parent={rootRef} datum={hoverPoint} xScale={xScale} yScale={yScale} />
                </div>
            </div>
        </ChartContext>
    )
}

const YAxis = memo(({
    yScale,
    ref,
}: {
    yScale: ScaleLinear<number, number, never>,
    ref: RefObject<SVGTextElement | null>
}) =>
{
    const { fontFamily, valueFmtOptions } = useContext(ChartContext)

    const [min, max] = extent(yScale.ticks()) as [number, number]
    const ts = ticks(min, max, 5)

    const fmt = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        maximumFractionDigits: valueFmtOptions.decimals,
        minimumFractionDigits: 0,
        notation: 'compact',
        compactDisplay: 'short',
    })

    const format = (n: number) => {
        let nn = +n
        if (nn < 10) {
            return d3format(`,.${valueFmtOptions.decimals}~f`)(n)
        }

        if (nn < 1_000_000) {
            return d3format(",.0f")(n)
        }

        return fmt.format(n)
    }

    return (
        <text
            ref={ref}
            textAnchor={'end'}
            opacity={0.6}
            fontSize={axisFontSize}
            fontWeight={500}
            letterSpacing={0.66}
            fontFamily={fontFamily}
            className={'fill-accent'}
        >
            {ts.map((tick, i) =>
            {
                const y = yScale(tick)
                return (
                    <tspan key={`label-${tick}`} x={0} y={y} alignmentBaseline={'middle'}>{format(tick)}</tspan>
                )
            })}
        </text>
    )
})

const YGrid = memo(({
    yScale,
    lineWidth,
}: {
    yScale: ScaleLinear<number, number>,
    lineWidth: number
}) =>
{
    const [min, max] = extent(yScale.ticks()) as [number, number]
    const ts = ticks(min, max, 5)

    return (
        <>
            {ts.map((tick, i) =>
            {
                const y = yScale(tick)
                return (
                    <line
                        key={`line-${tick}`}
                        x1={0}
                        x2={lineWidth}
                        y1={y + 2}
                        y2={y + 2}
                        strokeOpacity={0.2}
                        strokeWidth={0.15}
                        className={'stroke-accent'}
                    />
                )
            })}
        </>
    )
})

// thanks, @grok
const curveStepRounded: CurveFactoryLineOnly = (context) =>
{
    let x0: number | null, y0: number | null, x1: number | null, y1: number | null

    return {
        lineStart: function ()
        {
            x0 = null
            y0 = null
        },
        point: function (x, y)
        {
            x = +x
            y = +y

            if (x0 === null) {
                // First point
                context.moveTo(x, y)
            }
            else {
                // Draw the step with rounded corners
                if (y0 !== null && y !== y0) {
                    // Calculate control points for the step
                    const midX = x0 + (
                        x - x0
                    ) / 2 // Midpoint for step transition
                    const radius = Math.min(Math.abs(x - x0) / 2, Math.abs(y - y0) / 2, 2) // Corner radius
                    // (adjustable)

                    // Horizontal line to midpoint
                    context.lineTo(midX - radius, y0)
                    // Arc for the corner
                    context.arcTo(midX, y0, midX, y, radius)
                    // Vertical line to the next point
                    context.lineTo(
                        midX,
                        y - (
                              y > y0
                                  ? radius
                                  : -radius
                          ),
                    )
                    // Arc for the second corner
                    context.arcTo(midX, y, x, y, radius)
                    // Horizontal line to the final x
                    context.lineTo(x, y)
                }
                else {
                    // If no vertical change, just draw a straight line
                    context.lineTo(x, y)
                }
            }

            x0 = x
            y0 = y
        },
        lineEnd: function ()
        {
            // No additional action needed
        },
    }
}

const Line = memo(({
    data,
    xScale,
    yScale,
}: {
    data: LineSmallData,
    xScale: ScaleTime<number, number>,
    yScale: ScaleLinear<number, number>
}) =>
{
    const d = useMemo(() =>
    {
        return line<LineSmallDatum>()
                   .x(d => xScale(d[0]))
                   .y(d => yScale(d[1]))
                   .curve(curveStepRounded)(data) || ''
    }, [data, xScale, yScale])

    return (
        <>
            <defs>
                <linearGradient id="grad-chart-line-small" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopOpacity={0.3} style={{ stopColor: 'var(--color-accent)' }} />
                    <stop offset="100%" style={{ stopColor: 'var(--color-accent)' }} />
                </linearGradient>
            </defs>

            <path
                d={d}
                fill={'transparent'}
                stroke={`url(#grad-chart-line-small)`}
                strokeWidth={1}
                strokeLinecap={'round'}
                strokeLinejoin={'round'}
            />
        </>
    )
})

const Crosshair = memo(({
    datum,
    xScale,
    yScale,
    chartWidth,
    chartHeight,
}: {
    datum: LineSmallDatum | null,
    xScale: ScaleTime<number, number>,
    yScale: ScaleLinear<number, number>,
    chartWidth: number,
    chartHeight: number
}) =>
{
    if (!datum) {
        return (
            <></>
        )
    }

    const pointSize = 5

    const [x, y] = [xScale(datum[0]), yScale(datum[1])]
    const [xRect, yRect] = [x - pointSize / 2, y - pointSize / 2]

    return (
        <g className={'*:transition-transform *:ease-linear *:duration-50'}>
            <line
                strokeDasharray={1}
                opacity={0.4}
                x1={0}
                x2={chartWidth}
                style={{ transform: `translateY(${y}px)` }}
                className={'stroke-accent'}
            />
            <line
                strokeDasharray={1}
                opacity={0.4}
                y1={0}
                y2={chartHeight}
                style={{ transform: `translateX(${x}px)` }}
                className={'stroke-accent'}
            />
            <rect
                width={pointSize}
                height={pointSize}
                style={{ transform: `translate(${xRect}px, ${yRect}px)` }}
                className={'fill-accent'}
            />
        </g>
    )
})

const Tooltip = memo(({
    parent,
    datum,
    xScale,
    yScale,
}: {
    parent: RefObject<HTMLDivElement | null>,
    datum: LineSmallDatum | null,
    xScale: ScaleTime<number, number>,
    yScale: ScaleLinear<number, number>,
}) =>
{
    const { valueFmt, valueFmtOptions } = useContext(ChartContext)

    const tipRef = useRef<HTMLDivElement>(null)
    const tipSize = useResizeObserver(tipRef)

    const [tipPos, setTipPos] = useState({
        x: 0,
        y: 0,
    })

    const [x, y] = useMemo(() =>
    {
        if (!parent.current) {
            return [null, null]
        }

        if (datum === null) {
            return [null, null]
        }

        let [x, y] = [xScale(datum[0]), yScale(datum[1])]

        const {
            left,
            right,
        } = parent.current.getBoundingClientRect()
        // check screen bounds
        const tipDX = x - tipSize.width / 2
        if (left + tipDX - tipSize.width / 2 < 8) {
            x = 8 + tipSize.width / 2
        }
        else if (left + x + tipSize.width > document.documentElement.clientWidth - 8) {
            // - 16 instead of -8 because of scroll padding
            x = document.documentElement.clientWidth - 16 - left - tipSize.width
        }

        return [x, y]
    }, [parent.current, tipSize.width, datum])

    useEffect(() =>
    {
        if (x !== null && y !== null) {
            setTipPos({
                x,
                y,
            })
        }
    }, [x, y])

    return (
        <div
            className={cn(
                `relative`,
                'tooltip',
                `ease-linear duration-50`,
                datum === null && `sr-only invisible opacity-0`,
                'whitespace-nowrap',
            )}
            style={{
                transitionProperty: 'transform, visibility, opacity',
                transform: `translate(calc(-50% + ${tipPos.x}px), calc(-100% + ${tipPos.y - 8}px))`,
            }}
            aria-hidden={datum === null}
            ref={tipRef}
        >
            <span className={'opacity-60'}>{datum === null
                ? 'Xxx XX, XXXX'
                : datum[0].toLocaleString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                })}</span><br /><span>{datum === null
            ? '$X,XXX.XX'
            : valueFmt(datum[1], valueFmtOptions.decimals, valueFmtOptions.prefix)}</span>
        </div>
    )
})